import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Grid, Select, MenuItem, TextField, InputAdornment, Table, TableHead, TableRow, TableCell,
  TableBody, TableSortLabel, Avatar, Chip, Dialog, DialogTitle, DialogContent, IconButton, Skeleton, ButtonBase,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { analyticsApi } from '../services/api';
import { ErrorState } from '../components/common/States';
import EmptyState from '../components/common/EmptyState';
import ComingSoon from '../components/common/ComingSoon';
import SubTabs from '../components/common/SubTabs';
import StockChart from '../components/common/StockChart';
import Sparkline from '../components/common/Sparkline';
import InfoTooltip from '../components/common/InfoTooltip';
import { EXPLAIN } from '../utils/explain';
import { apiError, formatDateShort, formatNumber, formatRelativeMsk, pluralize } from '../utils/format';
import { hasSync, idPrefix } from '../utils/marketplaces';
import { tokens } from '../theme';

const THRESHOLD_KEY = 'marqit_stock_threshold';
const readThreshold = () => {
  try {
    const v = Number(window.localStorage.getItem(THRESHOLD_KEY));
    return [7, 14, 30].includes(v) ? v : 14;
  } catch (e) {
    return 14;
  }
};

const daysText = (d) => `${formatNumber(Math.floor(d))} ${pluralize(Math.floor(d), 'день', 'дня', 'дней')}`;

// How long the stock lasts at the current order rate, as a coloured label with an icon.
const DaysLeft = ({ item, threshold }) => {
  if (item.quantity <= 0 && item.orders > 0) {
    return <Chip size="small" icon={<WarningAmberRoundedIcon />} label="нет в наличии" sx={{ bgcolor: tokens.errorSoft, color: tokens.error, '& .MuiChip-icon': { color: tokens.error } }} />;
  }
  if (item.days_left == null) return <Typography variant="body2" color="text.secondary">нет заказов</Typography>;
  const d = item.days_left;
  if (d < 7) return <Chip size="small" icon={<WarningAmberRoundedIcon />} label={`≈ ${daysText(d)}`} sx={{ bgcolor: tokens.errorSoft, color: tokens.error, '& .MuiChip-icon': { color: tokens.error } }} />;
  if (d < threshold) return <Chip size="small" icon={<WarningAmberRoundedIcon />} label={`≈ ${daysText(d)}`} sx={{ bgcolor: tokens.warningSoft, color: tokens.warning, '& .MuiChip-icon': { color: tokens.warning } }} />;
  return <Chip size="small" icon={<CheckCircleRoundedIcon />} label={d > 365 ? 'больше года' : `≈ ${daysText(d)}`} sx={{ bgcolor: tokens.successSoft, color: tokens.success, '& .MuiChip-icon': { color: tokens.success } }} />;
};

const ProductPhoto = ({ item, size = 40 }) => (
  <Avatar variant="rounded" src={item.photo_url || undefined} alt="" sx={{ width: size, height: size * 1.3, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
    <ImageOutlinedIcon fontSize="small" />
  </Avatar>
);

const ProductDialog = ({ item, threshold, onClose, mp }) => (
  <Dialog open={Boolean(item)} onClose={onClose} maxWidth="md" fullWidth>
    {item && (
      <>
        <DialogTitle sx={{ display: 'flex', gap: 2, alignItems: 'center', pr: 7 }}>
          <ProductPhoto item={item} size={44} />
          <Box component="span" sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ display: 'block' }}>{item.title || 'Без названия'}</Box>
            <Box component="span" sx={{ display: 'block', fontSize: '0.875rem', fontWeight: 400, color: tokens.inkSoft }}>
              {item.vendor_code || '—'} · {idPrefix(mp)} {item.nm_id}
            </Box>
          </Box>
          <IconButton onClick={onClose} aria-label="Закрыть" sx={{ position: 'absolute', right: 16, top: 16 }}><CloseRoundedIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Остаток сейчас', `${formatNumber(item.quantity)} шт.`],
              ['Заказов в день', formatNumber(Math.round(item.orders_per_day * 10) / 10)],
              ['Хватит на', <DaysLeft key="d" item={item} threshold={threshold} />],
            ].map(([k, v]) => (
              <Grid item xs={12} sm={4} key={k}>
                <Box sx={{ p: 2, borderRadius: '12px', bgcolor: tokens.primaryTint, height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">{k}</Typography>
                  <Box sx={{ fontWeight: 700, fontSize: '1.25rem', mt: 0.5 }}>{v}</Box>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Остаток по дням</Typography>
          <StockChart points={item.history} height={200} label={`Остаток товара ${item.nm_id} по дням`} />
          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>По складам</Typography>
          {item.warehouses.length === 0 ? (
            <Typography color="text.secondary">{mp === 'ozon' ? 'Ни на складах Ozon, ни на ваших складах товара нет.' : 'Ни на складах WB, ни на ваших складах (FBS) товара нет.'}</Typography>
          ) : (
            <Table size="small">
              <TableBody>
                {item.warehouses.map((w) => (
                  <TableRow key={w.name}>
                    <TableCell>{w.name || 'Склад не указан'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(w.quantity)} шт.</TableCell>
                    <TableCell sx={{ width: '40%' }}>
                      <Box sx={{ height: 8, borderRadius: '16px', bgcolor: tokens.primarySoft }}>
                        <Box sx={{ height: '100%', borderRadius: '16px', bgcolor: tokens.primary, width: `${Math.max(2, (w.quantity / Math.max(item.quantity, 1)) * 100)}%` }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </>
    )}
  </Dialog>
);

const COLUMNS = [
  { key: 'quantity', label: 'Остаток', align: 'right' },
  { key: 'orders_per_day', label: 'Заказов в день', align: 'right' },
  { key: 'days_left', label: 'Хватит на', align: 'left' },
];

const StocksPage = () => {
  const { marketplace, accounts } = useOutletContext();
  const [accountId, setAccountId] = useState('');
  const [days, setDays] = useState('30');
  const [threshold, setThreshold] = useState(readThreshold);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'days_left', dir: 'asc' });
  const [open, setOpen] = useState(null);

  // only the latest request may fill the page: an answer for a previous cabinet is dropped
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!hasSync(marketplace)) return;
    const seq = ++loadSeq.current;
    setError('');
    try {
      const { data: d } = await analyticsApi.stocks({ days, ...(accountId ? { account_id: accountId } : {}), ...(marketplace === 'ozon' ? { marketplace: 'ozon' } : {}) });
      if (seq === loadSeq.current) setData(d);
    } catch (e) {
      if (seq === loadSeq.current) setError(apiError(e));
    }
  }, [marketplace, days, accountId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const changeThreshold = (v) => {
    setThreshold(v);
    try { window.localStorage.setItem(THRESHOLD_KEY, String(v)); } catch (e) { /* per-visit only */ }
  };

  const running = (it) => (it.quantity <= 0 && it.orders > 0) || (it.days_left != null && it.days_left < threshold);
  const low = useMemo(() => (data?.items || []).filter(running).sort((a, b) => (a.days_left ?? 0) - (b.days_left ?? 0)), [data, threshold]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = (data?.items || []).filter((p) => !q || String(p.nm_id).includes(q) || (p.vendor_code || '').toLowerCase().includes(q) || (p.title || '').toLowerCase().includes(q));
    if (filter === 'low') list = list.filter(running);
    if (filter === 'in') list = list.filter((p) => p.quantity > 0);
    if (filter === 'out') list = list.filter((p) => p.quantity <= 0);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }, [data, query, filter, sort, threshold]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasSync(marketplace)) return <ComingSoon marketplace={marketplace} what="Остатки" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const total = (data?.items || []).reduce((s, it) => s + it.quantity, 0);
  const inStock = (data?.items || []).filter((it) => it.quantity > 0).length;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2.5 }}>
        {accounts.length > 1 && (
          <Select size="small" value={accountId || 'all'} onChange={(e) => setAccountId(e.target.value === 'all' ? '' : e.target.value)} inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 220 }}>
            <MenuItem value="all">Все кабинеты</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        )}
        <SubTabs value={days} onChange={setDays} items={[{ key: '14', label: '14 дней' }, { key: '30', label: '30 дней' }, { key: '90', label: '90 дней' }]} />
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">Предупреждать, если хватит меньше чем на</Typography>
          <Select size="small" value={threshold} onChange={(e) => changeThreshold(Number(e.target.value))} inputProps={{ 'aria-label': 'Порог предупреждения' }}>
            {[7, 14, 30].map((d) => <MenuItem key={d} value={d}>{d} дней</MenuItem>)}
          </Select>
        </Stack>
      </Stack>

      {!data ? (
        <Stack spacing={2}><Skeleton variant="rounded" height={300} sx={{ borderRadius: '16px' }} /><Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} /></Stack>
      ) : (
        <>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                    <Box>
                      <Typography variant="body2" color="text.secondary">{marketplace === 'ozon' ? 'Всего на складах: Ozon (FBO) и ваших (FBS, rFBS)' : 'Всего на складах: WB (FBO) и ваших (FBS)'}</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '2rem', lineHeight: 1.2 }}>{formatNumber(total)} шт.</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatNumber(inStock)} {pluralize(inStock, 'товар', 'товара', 'товаров')} в наличии
                        {data.snapshot_at ? ` · обновлено ${formatRelativeMsk(data.snapshot_at)}` : ''}
                      </Typography>
                    </Box>
                    <InfoTooltip exclamation size={20} title={EXPLAIN.stocks.short} heading={EXPLAIN.stocks.title} details={EXPLAIN.stocks.details} sources={EXPLAIN.stocks.sources} />
                  </Stack>
                  <StockChart points={data.total} label="Общий остаток по дням" />
                  {data.total.length < 7 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      MARQIT сохраняет остаток каждый день начиная с {data.total[0] ? formatDateShort(data.total[0].day) : 'первой загрузки'} — график будет заполняться с каждым днём.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card sx={{ height: '100%', ...(low.length ? { borderColor: tokens.warning } : {}) }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    {low.length ? <WarningAmberRoundedIcon sx={{ color: tokens.warning }} /> : <CheckCircleRoundedIcon sx={{ color: tokens.success }} />}
                    <Typography variant="h3">{low.length ? `Заканчивается: ${low.length}` : 'Запасов достаточно'}</Typography>
                  </Stack>
                  {low.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      При текущей скорости заказов всех товаров хватит больше чем на {threshold} дней.
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {low.slice(0, 6).map((it) => (
                        <ButtonBase key={`${it.account_id}-${it.nm_id}`} onClick={() => setOpen(it)}
                          sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1, borderRadius: '10px', textAlign: 'left', justifyContent: 'flex-start', '&:hover': { bgcolor: tokens.primaryTint } }}>
                          <ProductPhoto item={it} size={32} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{it.title || it.vendor_code || `${idPrefix(marketplace)} ${it.nm_id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">осталось {formatNumber(it.quantity)} шт.</Typography>
                          </Box>
                          <DaysLeft item={it} threshold={threshold} />
                        </ButtonBase>
                      ))}
                      {low.length > 6 && (
                        <ButtonBase onClick={() => setFilter('low')} sx={{ alignSelf: 'flex-start', px: 1, py: 0.5, borderRadius: '8px', color: tokens.primaryDark, fontWeight: 600, fontSize: '0.875rem' }}>
                          Показать все {low.length}
                        </ButtonBase>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <TextField size="small" placeholder="Поиск: артикул, название" value={query} onChange={(e) => setQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                  inputProps={{ 'aria-label': 'Поиск товара' }} sx={{ minWidth: 260 }} />
                <SubTabs value={filter} onChange={setFilter}
                  items={[{ key: 'all', label: 'Все' }, { key: 'low', label: 'Заканчиваются' }, { key: 'in', label: 'В наличии' }, { key: 'out', label: 'Нет в наличии' }]} />
              </Stack>
              {rows.length === 0 ? (
                <EmptyState title={data.items.length ? 'Ничего не найдено' : 'Остатков пока нет'}
                  description={data.items.length ? 'Измените поиск или фильтр.' : `Остатки появятся после первой загрузки данных со складов ${marketplace === 'ozon' ? 'Ozon' : 'WB'}.`} />
              ) : (
                <Box sx={{ overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
                  <Table size="small" aria-label="Остатки товаров">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 260 }}>Товар</TableCell>
                        {COLUMNS.map((c) => (
                          <TableCell key={c.key} align={c.align}>
                            <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key ? sort.dir : 'asc'}
                              onClick={() => setSort({ key: c.key, dir: sort.key === c.key && sort.dir === 'asc' ? 'desc' : 'asc' })}>
                              {c.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                        <TableCell>Склады</TableCell>
                        <TableCell sx={{ width: 140 }}>Динамика</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(0, 500).map((it) => (
                        <TableRow key={`${it.account_id}-${it.nm_id}`} hover onClick={() => setOpen(it)} sx={{ cursor: 'pointer' }}>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <ProductPhoto item={it} size={34} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: 320 }} noWrap title={it.title}>{it.title || 'Без названия'}</Typography>
                                <Typography variant="caption" color="text.secondary">{it.vendor_code || '—'} · {idPrefix(marketplace)} {it.nm_id}</Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatNumber(it.quantity)}</TableCell>
                          <TableCell align="right">{it.orders ? formatNumber(Math.round(it.orders_per_day * 10) / 10) : '—'}</TableCell>
                          <TableCell><DaysLeft item={it} threshold={threshold} /></TableCell>
                          <TableCell sx={{ maxWidth: 240 }}>
                            <Typography variant="body2" noWrap title={it.warehouses.map((w) => `${w.name}: ${w.quantity}`).join(', ')}>
                              {it.warehouses.length ? it.warehouses.slice(0, 2).map((w) => w.name).join(', ') : '—'}
                              {it.warehouses.length > 2 && <Box component="span" sx={{ color: tokens.inkMuted }}> +{it.warehouses.length - 2}</Box>}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Sparkline data={it.history.map((h) => ({ value: h.quantity, source: 'finance' }))} color={tokens.primary} height={32}
                              label={`Динамика остатка ${it.nm_id}`} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
      <ProductDialog item={open} threshold={threshold} onClose={() => setOpen(null)} mp={marketplace} />
    </Box>
  );
};

export default StocksPage;
